import React, { useEffect, useState, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { Provider } from 'react-redux'

import initI18n from './services/i18n'
import { store } from './services/redux'
import { BRAND_NAME } from './config'

const ConnectedApp = lazy(() => import('./App'))

// Type Definitions
interface LocationState {
  sectionId?: string
}

// PositionRestore Component
const PositionRestore: React.FC = () => {
  const location = useLocation()
  const [prevLocation, setPrevLocation] = useState(location.pathname)

  useEffect(() => {
    if (prevLocation !== location.pathname) {
      setPrevLocation(location.pathname)

      const state = location.state as LocationState // Casting location.state
      if (state?.sectionId) {
        setTimeout(() => {
          const element = document.getElementById(state.sectionId as string)
          if (element) element.scrollIntoView({ behavior: 'smooth' })
        }, 0)
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      }
    }
  }, [location, prevLocation])

  return null
}

// Get Theme for Brand Function
const getThemeForBrand = (brand: string): string => {
  if (brand === 'vwfs') return 'bluelabel'
  if (brand === 'vwcv' || brand === 'vw') return 'vw6'
  return brand
}

// Get Styles Function
const getStyles = (brand: string): Promise<void> => {
  const theme = getThemeForBrand(brand)
  return import(
    `@vwfs-bronson/bronson-${theme}/dist/css/style.min.css`
    //@ts-ignore
  ).then(() => import('./styles.scss'))
}

// Init Component
const Init: React.FC = () => {
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    Promise.all([
      getStyles(BRAND_NAME as string),
      initI18n(BRAND_NAME as string),
    ]).then(() => setLoaded(true))
  }, [])

  return loaded ? (
    <Suspense fallback={null}>
      <Provider store={store}>
        <BrowserRouter>
          <PositionRestore />
          <ConnectedApp />
        </BrowserRouter>
      </Provider>
    </Suspense>
  ) : null
}

// Enable Hot Module Reloading
//@ts-ignore
if (module.hot) {
  //@ts-ignore
  module.hot.accept(render)
}

// Render Function
function render(): void {
  const rootElement = document.getElementById('root')
  if (!rootElement) {
    throw new Error('Root element not found')
  }
  const root = createRoot(rootElement)
  root.render(<Init />)
}

render()
